import { mstCommon } from '@libs/domain';
import { OutputOrderField, SearchBasicInfo } from '@libs/templates/mst/components';
import { useMstDelete, useMstListSearch } from '@libs/templates/mst/hooks';
import type { MstListSearch, MstRegistration } from '@libs/templates/mst/models';
import { OutputOrder } from '@libs/templates/mst/models';
import type { DeleteFunction, ListRequestFunction } from '@libs/templates/mst/repositories';
import type { BaseMstSearchTmplProps } from '@libs/templates/mst/templates/MstTmpl.type';
import type { PageProps } from '@ps-smart-cp/ppfw-client';
import {
  GridD,
  MasterMaintenanceFooterButtonGroup,
  Template,
  getNameWithDefault,
  useDeleteDialog,
  useGridD,
  useLocalizedContents,
  useTry,
  useValidator,
} from '@ps-smart-cp/ppfw-client';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { CustomMstMaintenanceFooterButtonTemp } from './CustomMstMaintenanceFooterButtonTemp';

// TODO: カスタムマスタ取込・出力対応時に削除 (https://proship-infra.atlassian.net/browse/AFW-306)
interface CustomMstMaintenanceTempTmplProps<S extends MstListSearch, R extends MstRegistration>
  extends BaseMstSearchTmplProps<S, R> {
  deleteFunction: DeleteFunction;
}

export function CustomMstMaintenanceTempTmpl<S extends MstListSearch, R extends MstRegistration>({
  mstName,
  items,
  parsers,
  options,
  columns,
  listRequestFunction,
  initMst,
  deleteFunction,
  children,
  rerender,
}: CustomMstMaintenanceTempTmplProps<S, R> & PageProps) {
  const navigate = useNavigate();
  const { locale } = useLocalizedContents();
  const { confirm } = useDeleteDialog();

  const { search, ...gridParams } = useGridD<R, ListRequestFunction<MstListSearch, R>, number>(
    {
      rowsPerPage: 15,
      getGridSchema: listRequestFunction as ListRequestFunction<MstListSearch, R>,
      initialCondition: [initMst(), OutputOrder.init()],
    },
    { fetchDataOnMount: false },
  );
  const validator = useValidator();
  const { errorFields, validateThenTryRequest } = useTry();
  const mstSearch = useMstListSearch(initMst, items, parsers, options);
  const { doDelete } = useMstDelete(deleteFunction);
  const [outputOrder, setOutputOrder] = useState(OutputOrder.init);

  const onSearch = validateThenTryRequest(
    () => validator.with(mstSearch.mstSearch, items).execute(),
    async () => await search(mstSearch.mstSearch, outputOrder),
  );

  const onChange = () => navigate(`modify/${[...gridParams.selectedRows][0]}`);

  const onDuplicate = () => {
    const row = gridParams.gridSchema.rows.find((row) => row.id === [...gridParams.selectedRows][0]);
    if (!row) return;

    navigate('register', { state: { mst: { ...row, code: '' } } });
  };

  const onDelete = async () => {
    if (!(await confirm())) return;

    await doDelete(gridParams.gridSchema.rows.filter(({ id }) => gridParams.selectedRows.has(id)));
    await search(mstSearch.mstSearch, outputOrder);
  };

  return (
    <Template
      title={
        getNameWithDefault(mstName.displayName, locale) +
        getNameWithDefault(mstCommon.titleNames.maintenance.displayName, locale)
      }
      footerButtonGroup={
        <MasterMaintenanceFooterButtonGroup
          onNew={() => navigate('register')}
          onChange={onChange}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          deleteDisabled={gridParams.selectedRows.size !== 1}
          changeDisabled={gridParams.selectedRows.size !== 1}
          duplicateDisabled={gridParams.selectedRows.size !== 1}
        />
      }
    >
      <SearchBasicInfo {...mstSearch} errorFields={errorFields}>
        {children}
      </SearchBasicInfo>
      <OutputOrderField outputKey={outputOrder} setOutputKey={setOutputOrder} />
      <div style={{ height: '30px' }}>
        <CustomMstMaintenanceFooterButtonTemp onSearch={onSearch} onReset={rerender} />
      </div>
      <GridD columns={columns} {...gridParams} nonRowsFallback="message" rowKeyGetter={(r) => r.id} />
    </Template>
  );
}
